<template>
  <div v-if="disabled" class="uk-flex uk-flex-middle" style="color: black">
    <div class="uk-text-bold">{{ label }}</div>
    <div class="uk-margin-small-left">{{ dateTimeString }}</div>
  </div>
  <div v-else class="uk-flex uk-flex-middle" style="color: black">
    <div
      class="uk-text-bold"
      :class="{
        'uk-text-muted': disabled,
      }"
    >
      {{ label }}
    </div>
    <input
      style="border-right: none; border-radius: 5px 0px 0px 5px"
      class="uk-width-1-4 uk-input uk-margin-small-left uk-display-inline uk-flex-auto date-input"
      :class="{
        error: timeThresholdError,
      }"
      type="date"
      v-model="date"
      :disabled="disabled"
      @change="handleDateTimeChange"
    />
    <select
      ref="timeThresholdSelect"
      class="uk-select uk-display-inline custom-select"
      v-model="timeThreshold"
      :disabled="disabled"
      @change="handleTimeThresholdChanged"
    >
      <option v-for="opt in respondTimeOptions" :key="opt.value" :value="opt.value">
        {{ opt.label }}
      </option>
    </select>
    <input
      class="uk-width-1-4 uk-input uk-margin-small-left uk-display-inline uk-flex-auto"
      type="time"
      v-model="time"
      :disabled="disabled"
      @change="handleDateTimeChange"
    />
  </div>
</template>

<script>
import { EventBus } from '@/assets/js/event-bus';
import { mapGetters } from 'vuex';

export default {
  name: 'DateTimePicker',
  props: {
    label: String,
    respondTimeOptions: Array,
    defaultTimeThreshold: Number,
    defaultTime: String,
    disabled: Boolean,
  },
  data() {
    return {
      timeThresholdError: false,
      date: null,
      time: null,
      timeThreshold: null,
    };
  },

  emits: ['timeThresholdChange'],

  mounted() {
    if (this.defaultTimeThreshold)
      this.setupDateAndTime(this.defaultTimeThreshold, this.defaultTime);
    EventBus.on('generalinstruction.dateerror', this.showDateError);
  },

  beforeUnmount() {
    EventBus.off('generalinstruction.dateerror', this.showDateError);
  },

  computed: {
    ...mapGetters(['getAdjustedTime']),
    dateTimeString() {
      let dateString = this.date?.split('-') || [];
      dateString = `${dateString[1]}/${dateString[2]}/${dateString[0]}`;
      let timeString = new Date(this.date + ' ' + this.time).toLocaleTimeString('en-GB', {
        hour: '2-digit',
        minute: 'numeric',
        hour12: true,
      });
      return `${dateString} ${timeString}`;
    },
  },

  methods: {
    setupDateAndTime(value, date) {
      this.timeThreshold = value;

      //
      // if threshold is a date/time (ISO format)
      if (date && date.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}.\d{3}Z$/)) {
        const inputDate = new Date(date);
        const formattedDate = this.getAdjustedTime(inputDate, value);
        this.setFormattedDateTime(formattedDate);
      }
      //
      // if threshold is numeric
      else {
        this.setDateAndTimeFromThreshold(value);
      }
    },

    handleTimeThresholdChanged(event) {
      const value = event.target.selectedOptions[0].value;
      this.timeThresholdError = false;
      //
      // get values formatted for form inputs
      this.setDateAndTimeFromThreshold(value);
      this.$emit('timeThresholdChange', value);
    },

    handleDateTimeChange() {
      const dateSubtract = new Date(`${this.date} ${this.time}`) - new Date();
      if (dateSubtract > 0) {
        let mins = Math.floor(dateSubtract / 60000);
        let hrs = Math.floor(mins / 60);
        mins = mins % 60;
        let threshold = hrs + mins / 100;
        this.$emit('timeThresholdChange', threshold);
      } else {
        //
        // If past date, return 0 to prompt error
        this.$emit('timeThresholdChange', 0);
      }
    },

    setDateAndTimeFromThreshold(threshold) {
      //
      // add threshold to the current date
      let currDate = new Date();
      const formDate = this.getAdjustedTime(currDate, threshold);
      this.setFormattedDateTime(formDate);
    },

    setFormattedDateTime(dateToFormat) {
      //
      // Format date
      // we need a local date first, in order for the ISO format conversion not to change the timezone
      let localeDate = dateToFormat.toLocaleDateString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      });
      const date = new Date(localeDate).toISOString().split('T')[0];
      //
      // Format time
      const time = dateToFormat.toLocaleTimeString(
        'en-GB', // GB locale format used in order to get time in 24-hour format,
        // (rather than the AM/PM we get from the US format)
        { hour: '2-digit', minute: 'numeric', second: 'numeric' }
      );

      this.date = date;
      this.time = time;
    },

    showDateError() {
      this.timeThresholdError = true;
    },
  },
};
</script>

<style lang="sass" scoped>
.custom-select
  width: 20px
  background: rgb(248, 248, 248)
  border: 2px solid #959595
  border-radius: 5px
  font-size: $global-small-font-size
  border-radius: 0px 4px 5px 0px
  background-position: 50% 50%
  padding: 0
.custom-select
  &:focus
    outline: none
    background-color: #fff
    color: #666
    border-color: #7cb6c9
.date-input
  position: relative
  &.error::after
    content: 'Date must be in the future.'
    display: block
    color: red
    position: absolute
    left: 0
    top: 98%
    width: 200%
</style>
